import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  IconButton,
  Stack,
  CircularProgress,
} from "@mui/material";
import { Close as CloseIcon, Print as PrintIcon } from "@mui/icons-material";
import { useTranslation } from "../../../../hooks/useTranslation";
import { useRef, useState } from "react";
import { useReactToPrint } from "react-to-print";

interface ShippingLabelModalProps {
  open: boolean;
  onClose: () => void;
  trackingNumber: string | undefined;
  labelUrl: string | undefined;
  carrierName: string | undefined;
}

export const ShippingLabelModal = ({
  open,
  onClose,
  trackingNumber,
  labelUrl,
  carrierName,
}: ShippingLabelModalProps) => {
  const { translate, translateWithoutPrefix } = useTranslation(
    "pages.orderDetails.prepareShipment.labelModal"
  );
  const [imageLoading, setImageLoading] = useState(true);
  const [imageError, setImageError] = useState(false);
  const imageRef = useRef<HTMLImageElement>(null);
  const reactToPrintFn = useReactToPrint({ contentRef: imageRef });

  const handleImageLoad = () => {
    setImageLoading(false);
  };

  const handleImageError = () => {
    setImageLoading(false);
    setImageError(true);
  };

  const handleClose = () => {
    setImageLoading(true);
    setImageError(false);
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Stack direction="row" spacing={1} alignItems="center">
          <Typography variant="h6">{translate("title")}</Typography>
          <Box flexGrow={1} />
          <IconButton onClick={handleClose} aria-label="close">
            <CloseIcon />
          </IconButton>
        </Stack>

        <Typography variant="body2" color="textSecondary" sx={{ mt: 1 }}>
          {translate("trackingNumber")}: {trackingNumber} |{" "}
          {translate("carrier")}: {carrierName}
        </Typography>
      </DialogTitle>

      <DialogContent>
        {labelUrl ? (
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              minHeight: 400,
              position: "relative",
            }}
          >
            {imageLoading && (
              <Box
                sx={{
                  position: "absolute",
                  top: "50%",
                  left: "50%",
                  transform: "translate(-50%, -50%)",
                }}
              >
                <CircularProgress />
              </Box>
            )}

            {imageError ? (
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                  height: 400,
                  textAlign: "center",
                }}
              >
                <Typography variant="h6" color="error" gutterBottom>
                  {translate("imageLoadError")}
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  {translate("imageLoadErrorDescription")}
                </Typography>
              </Box>
            ) : (
              <img
                ref={imageRef}
                src={labelUrl}
                alt={translate("shippingLabel")}
                style={{
                  maxWidth: "100%",
                  height: "auto",
                  display: imageLoading ? "none" : "block",
                }}
                onLoad={handleImageLoad}
                onError={handleImageError}
              />
            )}
          </Box>
        ) : (
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              height: 400,
            }}
          >
            <CircularProgress />
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ mx: 2, my: 1 }}>
        <Button onClick={handleClose} style={{ textTransform: "none" }}>
          {translateWithoutPrefix("buttons.close")}
        </Button>
        <Button
          variant="contained"
          onClick={reactToPrintFn}
          disabled={!labelUrl || imageError}
          startIcon={<PrintIcon />}
          style={{ textTransform: "none" }}
        >
          {translate("printLabel")}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
